import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { priceOnFinish } from '../../supplementary_components/priceInputDelegate';
import { API_ROOT, sendRequest } from '../../supplementary_components/requests';
import { addNotification } from '../../supplementary_components/store';
import { LoadingIcon } from '../../supplementary_components/loading_icon';
import listStyles from '../../general_components/content_management/list_view/listView.module.scss';
import reportStyles from './reports.module.scss';
import { mergeById, normalizeDataset } from './expenses';

const FUEL_CARD_TX_GRID_COLUMNS = '1fr 0.5fr 0.5fr 1fr 0.65fr minmax(12rem, 2fr)';

const LineItemsRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
`;

const LineItemChip = styled.div`
    padding: 0.2rem 0.45rem;
    border: 1px solid var(--lighter-gray);
    border-radius: 0.35rem;
    font-size: 0.85rem;
    white-space: nowrap;
`;

const CopyableLocation = styled.span`
    cursor: ${(props) => (props.$clickable ? 'pointer' : 'default')};
    text-decoration: ${(props) => (props.$clickable ? 'underline' : 'none')};
`;

function formatDateTime(value) {
    if (!value) return '-';
    const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
    if (!match) return String(value);
    const month = match[2];
    const day = match[3];
    const hour = match[4];
    const minute = match[5];
    return `${month}/${day} ${hour}:${minute}`;
}

function formatLocation(row) {
    const city = row?.locationCity?.trim();
    const state = row?.locationState?.trim();
    if (city && state) return `${city}, ${state}`;
    return city || state || '-';
}

function copyLatLng(latLng) {
    if (!latLng) return;
    navigator.clipboard.writeText(latLng).then(
        () => addNotification({ message: 'Coordinates copied', type: 'success' }),
        () => addNotification({ message: 'Failed to copy coordinates', type: 'alert' }),
    );
}

function formatPpu(ppu) {
    if (ppu == null || !Number.isFinite(Number(ppu))) return null;
    return `$${Number(ppu).toFixed(3)}`;
}

function formatLineItemLabel(item) {
    const category = item?.category ?? '?';
    if (category === 'SCLE') return 'SCALE';
    const qty = item?.quantity;
    const qtyText = qty != null && Number.isFinite(Number(qty)) ? Number(qty).toFixed(2) : '-';
    const ppuText = formatPpu(item?.ppu);
    return ppuText ? `${category} ${qtyText} gal (${ppuText})` : `${category} ${qtyText} gal`;
}

function compareTransactions(a, b) {
    const byId = Number(b.id ?? 0) - Number(a.id ?? 0);
    if (byId !== 0) return byId;
    return String(b.dateTime || '').localeCompare(String(a.dateTime || ''));
}

const DISPLAY_COLUMNS = [
    {
        label: 'Card Number',
        columnName: 'cardNumber',
        render: (row) => (
            <span>{row?.cardNumber != null && String(row.cardNumber).trim() !== '' ? row.cardNumber : '-'}</span>
        ),
    },
    {
        label: 'Datetime',
        columnName: 'dateTime',
        render: (row) => <span>{formatDateTime(row?.dateTime)}</span>,
    },
    {
        label: 'Truck',
        columnName: 'truck',
        render: (row) => <span>{row?.truck?.truckNumber ?? '-'}</span>,
    },
    {
        label: 'Location',
        columnName: 'location',
        render: (row) => {
            const latLng = row?.latLng?.trim() || null;
            return (
                <CopyableLocation
                    $clickable={!!latLng}
                    title={latLng ? `Click to copy: ${latLng}` : undefined}
                    onClick={() => copyLatLng(latLng)}
                >
                    {formatLocation(row)}
                </CopyableLocation>
            );
        },
    },
    {
        label: 'Funded Total',
        columnName: 'fundedTotal',
        render: (row) => (
            <span>{row?.fundedTotal != null ? priceOnFinish(row.fundedTotal) : '-'}</span>
        ),
    },
    {
        label: 'Items',
        columnName: 'items',
        render: (row) => {
            const items = Array.isArray(row?.items) ? row.items : [];
            if (items.length === 0) return <span>-</span>;
            return (
                <LineItemsRow>
                    {items.map((item, index) => (
                        <LineItemChip key={item?.id ?? `${row.id}-${index}`}>
                            {formatLineItemLabel(item)}
                        </LineItemChip>
                    ))}
                </LineItemsRow>
            );
        },
    },
];

function fetchFuelCardTransactionsPage(pageNumber, signal) {
    return new Promise((resolve) => {
        sendRequest(
            'GET',
            `${API_ROOT}fuelcardtransactions?pageNumber=${pageNumber}`,
            signal,
            (res) => resolve(res ?? {}),
            () => resolve({ dataset: [], nextPageAvailable: false, currentPageNumber: pageNumber }),
            null,
        );
    });
}

function renderColumnCell(col, row) {
    if (col.render) {
        return col.render(row);
    }
    const text = row[col.columnName];
    return <span>{text ?? '-'}</span>;
}

export const FuelCardTransactionsPage = () => {
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadMoreBusy, setLoadMoreBusy] = useState(false);
    const [error, setError] = useState(null);
    const [hasMore, setHasMore] = useState(false);
    const [currentPageNumber, setCurrentPageNumber] = useState(0);

    const sortedTransactions = useMemo(
        () => [...transactions].sort(compareTransactions),
        [transactions],
    );

    useEffect(() => {
        let cancelled = false;
        const ac = new AbortController();

        const run = async () => {
            setLoading(true);
            setError(null);
            try {
                const res = await fetchFuelCardTransactionsPage(0, ac.signal);
                if (cancelled) return;
                setTransactions(normalizeDataset(res));
                setCurrentPageNumber(res.currentPageNumber ?? 0);
                setHasMore(!!res.nextPageAvailable);
            } catch (e) {
                if (!cancelled) setError(e);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        run();
        return () => {
            cancelled = true;
            ac.abort();
        };
    }, []);

    const onLoadMore = useCallback(async () => {
        if (!hasMore || loadMoreBusy || loading) return;
        setLoadMoreBusy(true);
        const ac = new AbortController();
        const nextPage = currentPageNumber + 1;
        try {
            const res = await fetchFuelCardTransactionsPage(nextPage, ac.signal);
            const rows = normalizeDataset(res);
            setTransactions((prev) => mergeById(prev, rows).merged);
            setCurrentPageNumber(res.currentPageNumber ?? nextPage);
            setHasMore(!!res.nextPageAvailable);
        } finally {
            setLoadMoreBusy(false);
        }
    }, [currentPageNumber, hasMore, loadMoreBusy, loading]);

    if (error) {
        return (
            <div className="errorMessage">{error.customError ? error.customError : 'Something went wrong'}</div>
        );
    }

    if (loading) {
        return <LoadingIcon state={true} height="10rem" />;
    }

    return (
        <div className={reportStyles.mainContainer}>
            <div className={listStyles.pageHeader}>
                <div>Fuel Card Transactions</div>
            </div>

            <div
                className={listStyles.container}
                // eslint-disable-next-line
                style={{ ['--list-columns']: FUEL_CARD_TX_GRID_COLUMNS }}
            >
                <div
                    className={listStyles.listViewHeaderWrapper}
                    style={{ gridTemplateColumns: 'var(--list-columns)' }}
                >
                    {DISPLAY_COLUMNS.map((col) => (
                        <div key={col.columnName} className={listStyles.listHeaderLabel}>
                            <span className={listStyles.labelText}>{col.label}</span>
                        </div>
                    ))}
                </div>

                {sortedTransactions.length === 0 ? (
                    <div style={{ display: 'grid', gridTemplateColumns: 'var(--list-columns)', width: '100%' }}>
                        <div className={reportStyles.expensesEmptyBanner} style={{ gridColumn: '1 / -1' }}>
                            No fuel card transactions yet.
                        </div>
                    </div>
                ) : (
                    sortedTransactions.map((row) => (
                        <div
                            key={row.id}
                            className={listStyles.row}
                            style={{ gridTemplateColumns: 'var(--list-columns)' }}
                        >
                            {DISPLAY_COLUMNS.map((col) => (
                                <div key={col.columnName}>
                                    {renderColumnCell(col, row)}
                                </div>
                            ))}
                        </div>
                    ))
                )}
            </div>

            {hasMore ? (
                <div className={listStyles.loadMoreButtonWrapper}>
                    <div
                        className={loadMoreBusy ? 'loading' : ''}
                        onClick={() => {
                            if (!loadMoreBusy) onLoadMore();
                        }}
                    >
                        {loadMoreBusy ? 'Loading…' : 'Load more'}
                    </div>
                </div>
            ) : sortedTransactions.length > 0 ? (
                <div className={listStyles.loadMoreButtonWrapper}>
                    <div className={listStyles.noMoreToload}>No more transactions to load</div>
                </div>
            ) : null}
        </div>
    );
};
